/**
 * InfoSnackbar — brief informational toast.
 *
 * Usage:
 * ```tsx
 * const [open, setOpen] = useState(false);
 * <InfoSnackbar
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   message="No meetings scheduled for today."
 * />
 * ```
 */
import * as React from 'react';
import { Snackbar, Box, Typography, IconButton, type SnackbarOrigin } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

export interface InfoSnackbarProps {
  open: boolean;
  message: React.ReactNode;
  onClose: () => void;
  autoHideDuration?: number;
  anchorOrigin?: SnackbarOrigin;
}

const FONT_FAMILY = '"Inter Tight", "Inter", system-ui, sans-serif';

const InfoSnackbar: React.FC<InfoSnackbarProps> = ({
  open,
  message,
  onClose,
  autoHideDuration = 5000,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
}) => {
  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    onClose();
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={anchorOrigin}
      sx={{ '& .MuiPaper-root': { p: 0, minWidth: 0 } }}
    >
      <Box
        role="status"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: '#1A2048',
          color: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0px 16px 40px -8px rgba(36, 44, 99, 0.35)',
          px: 2,
          py: 1.25,
          minWidth: 360,
          maxWidth: 560,
          fontFamily: FONT_FAMILY,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20, color: '#E3D8BF', flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1,
            fontFamily: FONT_FAMILY,
            fontSize: '0.875rem',
            color: '#FFFFFF',
            '& strong': { fontWeight: 600, color: '#FFFFFF' },
          }}
        >
          {message}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            color: '#E3D8BF',
            p: 0.5,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};

export default InfoSnackbar;
